import { supabase } from './supabase'

export interface InvoiceData {
  invoice_number: string
  church_id: string
  church_name: string
  church_address?: string
  church_email?: string
  church_phone?: string

  // Invoice details
  issue_date: string
  due_date: string
  status: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled'

  // Subscription details
  plan: 'starter' | 'growth' | 'leadership'
  billing_cycle: 'annual' | 'quarterly'
  period_start: string
  period_end: string

  // Line items
  items: InvoiceLineItem[]

  // Amounts
  subtotal: number
  discount_amount: number
  discount_percentage: number
  tax_amount: number
  tax_percentage: number
  total_amount: number
  currency: string

  // Payment details
  payment_method?: string
  payment_date?: string
  transaction_id?: string

  // Notes
  notes?: string
  terms_and_conditions?: string
}

export interface InvoiceLineItem {
  description: string
  quantity: number
  unit_price: number
  amount: number
}

export interface ReceiptData extends Omit<InvoiceData, 'due_date' | 'status'> {
  receipt_number: string
  paid_date: string
  payment_method: string
  transaction_id: string
}

/**
 * Generate an invoice number
 */
export function generateInvoiceNumber(): string {
  const date = new Date()
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0')
  return `INV-${year}${month}-${random}`
}

/**
 * Generate a receipt number
 */
export function generateReceiptNumber(): string {
  const date = new Date()
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0')
  return `RCT-${year}${month}-${random}`
}

/**
 * Get pricing for a plan
 */
export function getPlanPricing(plan: string, billingCycle: string): number {
  const pricing = {
    starter: { monthly: 199, annual: 1910, quarterly: 597 },
    growth: { monthly: 499, annual: 4790, quarterly: 1497 },
    leadership: { monthly: 1499, annual: 14390, quarterly: 4497 },
  }

  return pricing[plan as keyof typeof pricing]?.[billingCycle as keyof typeof pricing.starter] || 0
}

/**
 * Get plan display name
 */
export function getPlanDisplayName(plan: string): string {
  const names: Record<string, string> = {
    starter: 'Starter Plan',
    growth: 'Growth Plan',
    leadership: 'Leadership Plan',
  }
  return names[plan] || plan
}

/**
 * Create an invoice for a subscription
 */
export async function createInvoice(churchId: string): Promise<InvoiceData | null> {
  try {
    // Get church and subscription data
    const { data: church, error: churchError } = await supabase
      .from('churches')
      .select('id, name, address, email, phone')
      .eq('id', churchId)
      .single()

    if (churchError) throw churchError

    const { data: subscription, error: subError } = await supabase
      .from('church_subscriptions')
      .select('*')
      .eq('church_id', churchId)
      .single()

    if (subError) throw subError

    // Calculate dates
    const issueDate = new Date()
    const dueDate = new Date()
    dueDate.setDate(dueDate.getDate() + 30) // 30 days to pay

    const periodStart = subscription.current_period_start || new Date().toISOString()
    const periodEnd = subscription.current_period_end || new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString()

    // Calculate amounts
    const unitPrice = getPlanPricing(subscription.plan, subscription.billing_cycle || 'annual')
    const subtotal = unitPrice
    const discountAmount = subtotal * (subscription.discount_percentage / 100 || 0)
    const taxAmount = 0 // Churches are typically tax-exempt
    const totalAmount = subtotal - discountAmount + taxAmount

    // Build line items
    const items: InvoiceLineItem[] = [
      {
        description: `${getPlanDisplayName(subscription.plan)} - ${subscription.billing_cycle === 'annual' ? 'Annual' : 'Quarterly'} Subscription`,
        quantity: 1,
        unit_price: unitPrice,
        amount: unitPrice,
      }
    ]

    if (subscription.early_adopter_discount) {
      items.push({
        description: 'Early Adopter Discount',
        quantity: 1,
        unit_price: -discountAmount,
        amount: -discountAmount,
      })
    }

    // Create invoice object
    const invoice: InvoiceData = {
      invoice_number: generateInvoiceNumber(),
      church_id: church.id,
      church_name: church.name,
      church_address: church.address || undefined,
      church_email: church.email || undefined,
      church_phone: church.phone || undefined,

      issue_date: issueDate.toISOString(),
      due_date: dueDate.toISOString(),
      status: 'sent',

      plan: subscription.plan,
      billing_cycle: subscription.billing_cycle || 'annual',
      period_start: periodStart,
      period_end: periodEnd,

      items,

      subtotal,
      discount_amount: discountAmount,
      discount_percentage: subscription.discount_percentage || 0,
      tax_amount: taxAmount,
      tax_percentage: 0,
      total_amount: totalAmount,
      currency: subscription.currency || 'GHS',

      notes: 'Thank you for your subscription to FaithYoke!',
      terms_and_conditions: 'Payment is due within 30 days. All subscriptions are non-refundable.',
    }

    return invoice
  } catch (error) {
    console.error('Error creating invoice:', error)
    return null
  }
}

/**
 * Create a receipt for a completed payment
 */
export async function createReceipt(
  churchId: string,
  paymentMethod: string,
  transactionId: string,
  paidDate?: string
): Promise<ReceiptData | null> {
  try {
    // First create the invoice data
    const invoice = await createInvoice(churchId)
    if (!invoice) return null

    // Convert to receipt
    const receipt: ReceiptData = {
      ...invoice,
      receipt_number: generateReceiptNumber(),
      paid_date: paidDate || new Date().toISOString(),
      payment_method: paymentMethod,
      transaction_id: transactionId,
    }

    // Remove invoice-specific fields
    delete (receipt as any).due_date
    delete (receipt as any).status

    return receipt
  } catch (error) {
    console.error('Error creating receipt:', error)
    return null
  }
}

/**
 * Generate printable HTML for invoice
 */
export function generateInvoiceHTML(invoice: InvoiceData): string {
  const issueDate = new Date(invoice.issue_date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
  const dueDate = new Date(invoice.due_date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
  const periodStart = new Date(invoice.period_start).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
  const periodEnd = new Date(invoice.period_end).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: invoice.currency,
    }).format(amount)
  }

  return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Invoice ${invoice.invoice_number}</title>
  <style>
    @media print {
      @page { margin: 0.5in; }
      body { margin: 0; }
      .no-print { display: none; }
    }

    body {
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      line-height: 1.6;
      color: #333;
      max-width: 800px;
      margin: 0 auto;
      padding: 20px;
    }

    .header {
      display: flex;
      justify-content: space-between;
      align-items: start;
      border-bottom: 3px solid #6366f1;
      padding-bottom: 20px;
      margin-bottom: 30px;
    }

    .company-info h1 {
      margin: 0;
      color: #6366f1;
      font-size: 28px;
    }

    .invoice-info {
      text-align: right;
    }

    .invoice-info h2 {
      margin: 0;
      font-size: 24px;
      color: #333;
    }

    .invoice-info p {
      margin: 5px 0;
      font-size: 14px;
    }

    .details-section {
      display: flex;
      justify-content: space-between;
      margin: 30px 0;
    }

    .bill-to, .invoice-details {
      flex: 1;
    }

    .bill-to h3, .invoice-details h3 {
      margin: 0 0 10px 0;
      color: #6366f1;
      font-size: 14px;
      text-transform: uppercase;
    }

    .bill-to p, .invoice-details p {
      margin: 5px 0;
      font-size: 14px;
    }

    table {
      width: 100%;
      border-collapse: collapse;
      margin: 30px 0;
    }

    th {
      background-color: #6366f1;
      color: white;
      padding: 12px;
      text-align: left;
      font-weight: 600;
    }

    td {
      padding: 12px;
      border-bottom: 1px solid #e5e7eb;
    }

    tr:hover {
      background-color: #f9fafb;
    }

    .text-right {
      text-align: right;
    }

    .totals-section {
      margin-left: auto;
      width: 300px;
      margin-top: 20px;
    }

    .totals-row {
      display: flex;
      justify-content: space-between;
      padding: 8px 0;
      font-size: 14px;
    }

    .totals-row.total {
      border-top: 2px solid #6366f1;
      font-weight: bold;
      font-size: 18px;
      color: #6366f1;
      padding-top: 12px;
      margin-top: 8px;
    }

    .footer {
      margin-top: 50px;
      padding-top: 20px;
      border-top: 1px solid #e5e7eb;
      font-size: 12px;
      color: #666;
    }

    .status-badge {
      display: inline-block;
      padding: 4px 12px;
      border-radius: 12px;
      font-size: 12px;
      font-weight: 600;
      text-transform: uppercase;
    }

    .status-sent { background-color: #dbeafe; color: #1e40af; }
    .status-paid { background-color: #d1fae5; color: #065f46; }
    .status-overdue { background-color: #fee2e2; color: #991b1b; }

    .print-button {
      position: fixed;
      top: 20px;
      right: 20px;
      padding: 10px 20px;
      background-color: #6366f1;
      color: white;
      border: none;
      border-radius: 6px;
      cursor: pointer;
      font-size: 14px;
      font-weight: 600;
    }

    .print-button:hover {
      background-color: #4f46e5;
    }
  </style>
</head>
<body>
  <button class="print-button no-print" onclick="window.print()">Print Invoice</button>

  <div class="header">
    <div class="company-info">
      <h1>FaithYoke</h1>
      <p>Church Management Platform</p>
      <p>support@faithyoke.com</p>
      <p>www.faithyoke.com</p>
    </div>
    <div class="invoice-info">
      <h2>INVOICE</h2>
      <p><strong>${invoice.invoice_number}</strong></p>
      <p><span class="status-badge status-${invoice.status}">${invoice.status}</span></p>
    </div>
  </div>

  <div class="details-section">
    <div class="bill-to">
      <h3>Bill To</h3>
      <p><strong>${invoice.church_name}</strong></p>
      ${invoice.church_address ? `<p>${invoice.church_address}</p>` : ''}
      ${invoice.church_email ? `<p>${invoice.church_email}</p>` : ''}
      ${invoice.church_phone ? `<p>${invoice.church_phone}</p>` : ''}
    </div>
    <div class="invoice-details">
      <h3>Invoice Details</h3>
      <p><strong>Issue Date:</strong> ${issueDate}</p>
      <p><strong>Due Date:</strong> ${dueDate}</p>
      <p><strong>Period:</strong> ${periodStart} - ${periodEnd}</p>
    </div>
  </div>

  <table>
    <thead>
      <tr>
        <th>Description</th>
        <th class="text-right">Quantity</th>
        <th class="text-right">Unit Price</th>
        <th class="text-right">Amount</th>
      </tr>
    </thead>
    <tbody>
      ${invoice.items.map(item => `
        <tr>
          <td>${item.description}</td>
          <td class="text-right">${item.quantity}</td>
          <td class="text-right">${formatCurrency(item.unit_price)}</td>
          <td class="text-right">${formatCurrency(item.amount)}</td>
        </tr>
      `).join('')}
    </tbody>
  </table>

  <div class="totals-section">
    <div class="totals-row">
      <span>Subtotal:</span>
      <span>${formatCurrency(invoice.subtotal)}</span>
    </div>
    ${invoice.discount_amount > 0 ? `
      <div class="totals-row">
        <span>Discount (${invoice.discount_percentage}%):</span>
        <span>-${formatCurrency(invoice.discount_amount)}</span>
      </div>
    ` : ''}
    ${invoice.tax_amount > 0 ? `
      <div class="totals-row">
        <span>Tax (${invoice.tax_percentage}%):</span>
        <span>${formatCurrency(invoice.tax_amount)}</span>
      </div>
    ` : ''}
    <div class="totals-row total">
      <span>Total:</span>
      <span>${formatCurrency(invoice.total_amount)}</span>
    </div>
  </div>

  <div class="footer">
    ${invoice.notes ? `<p><strong>Notes:</strong> ${invoice.notes}</p>` : ''}
    ${invoice.terms_and_conditions ? `<p><strong>Terms:</strong> ${invoice.terms_and_conditions}</p>` : ''}
    <p style="margin-top: 20px; text-align: center;">Thank you for your business!</p>
  </div>
</body>
</html>
  `
}

/**
 * Generate printable HTML for receipt
 */
export function generateReceiptHTML(receipt: ReceiptData): string {
  const paidDate = new Date(receipt.paid_date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: receipt.currency,
    }).format(amount)
  }

  return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Receipt ${receipt.receipt_number}</title>
  <style>
    @media print {
      @page { margin: 0.5in; }
      body { margin: 0; }
      .no-print { display: none; }
    }

    body {
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      line-height: 1.6;
      color: #333;
      max-width: 800px;
      margin: 0 auto;
      padding: 20px;
    }

    .header {
      text-align: center;
      border-bottom: 3px solid #10b981;
      padding-bottom: 20px;
      margin-bottom: 30px;
    }

    .header h1 {
      margin: 0;
      color: #10b981;
      font-size: 32px;
    }

    .header .receipt-number {
      font-size: 18px;
      color: #666;
      margin-top: 10px;
    }

    .paid-stamp {
      display: inline-block;
      padding: 10px 20px;
      background-color: #d1fae5;
      color: #065f46;
      border-radius: 8px;
      font-weight: 700;
      font-size: 24px;
      text-transform: uppercase;
      margin: 20px 0;
      border: 2px solid #10b981;
    }

    .details-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 30px;
      margin: 30px 0;
      padding: 20px;
      background-color: #f9fafb;
      border-radius: 8px;
    }

    .detail-group h3 {
      margin: 0 0 10px 0;
      color: #10b981;
      font-size: 14px;
      text-transform: uppercase;
    }

    .detail-group p {
      margin: 5px 0;
      font-size: 14px;
    }

    table {
      width: 100%;
      border-collapse: collapse;
      margin: 30px 0;
    }

    th {
      background-color: #10b981;
      color: white;
      padding: 12px;
      text-align: left;
      font-weight: 600;
    }

    td {
      padding: 12px;
      border-bottom: 1px solid #e5e7eb;
    }

    .text-right {
      text-align: right;
    }

    .total-paid {
      text-align: right;
      font-size: 24px;
      font-weight: bold;
      color: #10b981;
      margin: 20px 0;
      padding: 20px;
      background-color: #d1fae5;
      border-radius: 8px;
    }

    .footer {
      margin-top: 50px;
      padding-top: 20px;
      border-top: 1px solid #e5e7eb;
      text-align: center;
      font-size: 12px;
      color: #666;
    }

    .print-button {
      position: fixed;
      top: 20px;
      right: 20px;
      padding: 10px 20px;
      background-color: #10b981;
      color: white;
      border: none;
      border-radius: 6px;
      cursor: pointer;
      font-size: 14px;
      font-weight: 600;
    }

    .print-button:hover {
      background-color: #059669;
    }
  </style>
</head>
<body>
  <button class="print-button no-print" onclick="window.print()">Print Receipt</button>

  <div class="header">
    <h1>PAYMENT RECEIPT</h1>
    <div class="receipt-number">${receipt.receipt_number}</div>
    <div class="paid-stamp">PAID</div>
  </div>

  <div class="details-grid">
    <div class="detail-group">
      <h3>From</h3>
      <p><strong>${receipt.church_name}</strong></p>
      ${receipt.church_address ? `<p>${receipt.church_address}</p>` : ''}
      ${receipt.church_email ? `<p>${receipt.church_email}</p>` : ''}
      ${receipt.church_phone ? `<p>${receipt.church_phone}</p>` : ''}
    </div>
    <div class="detail-group">
      <h3>Payment Details</h3>
      <p><strong>Date Paid:</strong> ${paidDate}</p>
      <p><strong>Payment Method:</strong> ${receipt.payment_method}</p>
      <p><strong>Transaction ID:</strong> ${receipt.transaction_id}</p>
      <p><strong>Invoice:</strong> ${receipt.invoice_number}</p>
    </div>
  </div>

  <table>
    <thead>
      <tr>
        <th>Description</th>
        <th class="text-right">Quantity</th>
        <th class="text-right">Unit Price</th>
        <th class="text-right">Amount</th>
      </tr>
    </thead>
    <tbody>
      ${receipt.items.map(item => `
        <tr>
          <td>${item.description}</td>
          <td class="text-right">${item.quantity}</td>
          <td class="text-right">${formatCurrency(item.unit_price)}</td>
          <td class="text-right">${formatCurrency(item.amount)}</td>
        </tr>
      `).join('')}
    </tbody>
  </table>

  <div class="total-paid">
    Total Paid: ${formatCurrency(receipt.total_amount)} ${receipt.currency}
  </div>

  <div class="footer">
    <p><strong>FaithYoke</strong> | Church Management Platform</p>
    <p>support@faithyoke.com | www.faithyoke.com</p>
    <p style="margin-top: 20px;">This is an official receipt for your payment.</p>
    <p>Thank you for your subscription!</p>
  </div>
</body>
</html>
  `
}

/**
 * Open invoice in new window for printing
 */
export function printInvoice(invoice: InvoiceData): void {
  const html = generateInvoiceHTML(invoice)
  const printWindow = window.open('', '_blank')
  if (printWindow) {
    printWindow.document.write(html)
    printWindow.document.close()
  }
}

/**
 * Open receipt in new window for printing
 */
export function printReceipt(receipt: ReceiptData): void {
  const html = generateReceiptHTML(receipt)
  const printWindow = window.open('', '_blank')
  if (printWindow) {
    printWindow.document.write(html)
    printWindow.document.close()
  }
}

/**
 * Download invoice as HTML file
 */
export function downloadInvoice(invoice: InvoiceData): void {
  const html = generateInvoiceHTML(invoice)
  const blob = new Blob([html], { type: 'text/html' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `invoice-${invoice.invoice_number}.html`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

/**
 * Download receipt as HTML file
 */
export function downloadReceipt(receipt: ReceiptData): void {
  const html = generateReceiptHTML(receipt)
  const blob = new Blob([html], { type: 'text/html' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `receipt-${receipt.receipt_number}.html`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
